import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ArrowUpToLine, Undo2, Trash2 } from 'lucide-react';
import PainPointRow from './PainPointRow';
import { useTranslation } from '../../../hooks/useTranslation';

/**
 * One group of pain points: the AI-proposed title and scenario the
 * facilitator can change, the rank signals, the members, and the one
 * decision that matters — promote it to a muuttuja, or undo that.
 */

const Card = styled.div`
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$promoted ? 'rgba(34, 197, 94, 0.45)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(p) => (p.$promoted ? 'rgba(34, 197, 94, 0.06)' : 'rgba(255, 255, 255, 0.03)')};
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Head = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Rank = styled.div`
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
`;

const TitleInput = styled.input`
  flex: 1 1 260px;
  min-width: 0;
  background: transparent;
  color: #fff;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  &:hover, &:focus { border-color: #2c3444; outline: none; background: #0f131b; }
  &:disabled { color: #fff; }
`;

const Select = styled.select`
  background: #0f131b;
  color: #e8ecf2;
  border: 1px solid #2c3444;
  border-radius: 6px;
  padding: 5px 8px;
  font: inherit;
  font-size: 13px;
`;

const Signals = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  b { color: rgba(255, 255, 255, 0.85); font-weight: 600; }
`;

const Summary = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.45;
`;

const Members = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const Button = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$primary ? '#0b0d12' : 'rgba(255,255,255,0.8)')};
  background: ${(p) => (p.$primary ? '#4a9eff' : 'transparent')};
  border: 1px solid ${(p) => (p.$primary ? '#4a9eff' : '#2c3444')};
  &:disabled { opacity: 0.45; cursor: default; }
`;

const Promoted = styled.span`
  font-size: 13px;
  color: #22c55e;
  font-weight: 600;
`;

const Warn = styled.span`
  font-size: 12px;
  color: #f2c14e;
`;

export default function ClusterCard({ cluster: c, index, scenarios, others, busy, locked, onPatch, onMove, onPromote, onDemote, onDelete, overCap }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(c.title);
  const promoted = c.status === 'promoted';
  const disabled = busy || locked;
  const commitTitle = () => { if (title.trim() && title.trim() !== c.title) onPatch(c.id, { title: title.trim() }); else setTitle(c.title); };
  return (
    <Card $promoted={promoted}>
      <Head>
        <Rank title={t('preworkCuration.clusterCard.rankTitle')}>{index + 1}</Rank>
        <TitleInput value={title} onChange={(e) => setTitle(e.target.value)} onBlur={commitTitle}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    disabled={disabled || promoted} placeholder={t('preworkCuration.clusterCard.namePlaceholder')} />
        <Select value={c.scenarioId || ''} disabled={disabled || promoted} onChange={(e) => onPatch(c.id, { scenarioId: e.target.value || null })}>
          <option value="">{t('preworkCuration.clusterCard.scenarioPlaceholder')}</option>
          {scenarios.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </Select>
      </Head>
      <Signals>
        {c.rank.votes > 0 && <span><b>{c.rank.votes}</b> {t('preworkCuration.clusterCard.votes')}</span>}
        <span><b>{c.rank.participants}</b> {t('preworkCuration.clusterCard.participants')}</span>
        <span><b>{c.members.length}</b> {t('preworkCuration.clusterCard.painPoints')}</span>
        <span>{t('preworkCuration.clusterCard.disagreement')} <b>{Math.round(c.rank.disagreement * 100)} %</b></span>
        <span><b>{c.rank.obstacles}</b> {t('preworkCuration.clusterCard.obstacles')}</span>
        {overCap && !promoted && <Warn>{t('preworkCuration.clusterCard.overCap')}</Warn>}
      </Signals>
      {c.summary && <Summary>{c.summary}</Summary>}
      <Members>
        {c.members.map((m) => (
          <PainPointRow key={m.nodeId} painPoint={m} targets={others} locked={disabled || promoted}
                        onMove={(target) => onMove(c, m.nodeId, target)} />
        ))}
      </Members>
      <Actions>
        {promoted ? (
          <>
            <Promoted>{t('preworkCuration.clusterCard.promoted', { title: c.muuttujaText || c.title })}</Promoted>
            <Button onClick={() => onDemote(c.id)} disabled={disabled}><Undo2 size={14} /> {t('preworkCuration.clusterCard.undo')}</Button>
          </>
        ) : (
          <>
            <Button $primary onClick={() => onPromote(c.id)} disabled={disabled || !c.scenarioId || !c.title.trim()}>
              <ArrowUpToLine size={14} /> {t('preworkCuration.clusterCard.promote')}
            </Button>
            {!c.scenarioId && <Warn>{t('preworkCuration.clusterCard.pickScenarioFirst')}</Warn>}
            <Button onClick={() => onDelete(c.id)} disabled={disabled}><Trash2 size={14} /> {t('preworkCuration.clusterCard.dissolve')}</Button>
          </>
        )}
      </Actions>
    </Card>
  );
}
